import { equals } from '@everlutionsk/helpers';
import { MenuItem, MenuItemProps } from '@mui/material';
import { SelectInputProps as MuiSelectInputProps } from '@mui/material/Select/SelectInput';
import React, { ReactNode, memo, useMemo } from 'react';
import { Select } from './select';

export type SelectInputProps<T = any> = Omit<
  MuiSelectInputProps,
  | 'value'
  | 'onChange'
  | 'type'
  | 'select'
  | 'SelectProps'
  | 'placeholder'
  | 'multiple'
  | 'autoWidth'
  | 'native'
  | 'placeholder'
> & {
  /**
   * Selected value.
   */
  readonly value: T | undefined;

  /**
   * Called when user selects option from list.
   */
  readonly onChange: (value: T) => void;

  /**
   * Options to render.
   */
  readonly options: ReadonlyArray<SelectOption<T>>;

  /**
   * If `true`, compact vertical padding designed
   * for keyboard and mouse input will be used for options.
   */
  readonly dense?: boolean;

  /**
   * Helper text on bottom
   */
  readonly helperText?: string;

  /**
   * Label of the input
   * */
  readonly label: string;

  /**
   * Placeholder
   */
  readonly placeholder?: string;
};

export interface SelectOption<T = any> extends Omit<MenuItemProps, 'value' | 'children'> {
  readonly label: ReactNode;
  readonly value: T;

  /**
   * ReactNode which will be rendered as menu option.
   *
   * Fallbacks to `label` if not specified.
   */
  readonly node?: ReactNode;
}

/**
 * Material-UI Select with better options support.
 */
export function SelectInput<T>(props: SelectInputProps<T>) {
  const { value, options, ...restProps } = props;

  const valueIndex = useMemo(() => {
    return options.findIndex(option => equals(option.value, value));
  }, [value, options]);

  return <Control value={valueIndex} options={options} {...restProps} />;
}

interface ControlProps extends Omit<SelectInputProps, 'value'> {
  readonly value: number;
}

const Control = memo((props: ControlProps) => {
  const { options, value, onChange, dense, placeholder = '', ...muiProps } = props;

  return (
    <Select
      error={props.error ?? false}
      {...muiProps}
      displayEmpty={placeholder !== ''}
      onChange={event => {
        const index: number = event.target.value as any;
        onChange(options[index]?.value);
      }}
      value={value === -1 ? '' : value}
      renderValue={index => options[index as number]?.label ?? placeholder}
    >
      {options.map(({ value, label, node, ...menuItemProps }, i) => (
        <MenuItem value={i} key={i} dense={dense} {...menuItemProps}>
          {node ?? label}
        </MenuItem>
      ))}
    </Select>
  );
});
